import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Card from '../cards/Card';
import CardTextInput from '../EditableCard/CardTextInput';
import DeleteModal from '../DeleteModal/DeleteModal';
import StopSelect from '../StopSelect/StopSelect';
import {
  useCreateTestDailyDataMutation,
  useArchiveTestTourTemplateMutation,
  useGetRoutesQuery,
  useGetTestTourSourceStopsQuery,
  usePatchTestTourTemplateMutation,
} from '../drApi/drApi';
import { ReactComponent as CloseIcon } from '../../img/leave.svg';
import { ReactComponent as TrashIcon } from '../../img/bin_red.svg';
import { ReactComponent as PlusIcon } from '../../img/plus.svg';
import { ReactComponent as LoaderIcon } from '../../img/loader.svg';
import styles from './TestDailyDataCard.module.scss';

const emptyNewStop = () => ({
  customerNo: '',
  customerName: '',
  street: '',
  zip: '',
  place: '',
  latitude: '',
  longitude: '',
});

const getLocalDate = () => {
  const now = new Date();
  const offsetMilliseconds = now.getTimezoneOffset() * 60 * 1000;
  const localTime = new Date(now.getTime() - offsetMilliseconds);
  return localTime.toISOString().slice(0, 10);
};

const TestDailyDataCard = ({ house }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [creationMode, setCreationMode] = useState('custom');
  const [stopMode, setStopMode] = useState('existing');
  const [sourceRouteId, setSourceRouteId] = useState('');
  const [selectedSourceStopIds, setSelectedSourceStopIds] = useState([]);
  const [date, setDate] = useState(getLocalDate);
  const [name, setName] = useState('Testtour');
  const [freightOrderId, setFreightOrderId] = useState('TEST-FA');
  const [selectedStop, setSelectedStop] = useState(null);
  const [stops, setStops] = useState([]);
  const [newStop, setNewStop] = useState(emptyNewStop());
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [createDailyData, {
    isLoading,
    error,
    reset: resetMutation,
  }] = useCreateTestDailyDataMutation();
  const { data: routes = [] } = useGetRoutesQuery(house._id);
  const {
    data: sourceStopData,
    isFetching: isFetchingSourceStops,
  } = useGetTestTourSourceStopsQuery(sourceRouteId, { skip: !sourceRouteId });
  const [
    patchTestTourTemplate,
    { isLoading: isSavingSchedule, error: scheduleError },
  ] = usePatchTestTourTemplateMutation();
  const [archiveTestTour, { isLoading: isDeletingTemplate }] = useArchiveTestTourTemplateMutation();

  const testTours = useMemo(
    () => routes.filter((route) => route.isTestData && !route.testDataArchivedAt),
    [routes],
  );
  const sourceTours = useMemo(
    () => routes.filter((route) => route.stops > 0 && !route.testDataArchivedAt),
    [routes],
  );
  const selectedSourceRoute = useMemo(
    () => sourceTours.find((route) => route._id === sourceRouteId),
    [sourceRouteId, sourceTours],
  );
  const sourceStops = sourceStopData?.routeId === sourceRouteId ? sourceStopData.stops : [];
  const selectedIds = useMemo(
    () => stops.filter((stop) => stop.existingStopId).map((stop) => stop.existingStopId),
    [stops],
  );
  const newStopComplete = useMemo(() => {
    return Object.values(newStop).every((value) => String(value).trim());
  }, [newStop]);
  const canSubmit = Boolean(
    date
      && name.trim()
      && (creationMode === 'source' ? selectedSourceStopIds.length : stops.length)
      && (!house.isSAPHouse || freightOrderId.trim()),
  );

  const resetForm = () => {
    setDate(getLocalDate());
    setName('Testtour');
    setFreightOrderId('TEST-FA');
    setCreationMode('custom');
    setSourceRouteId('');
    setSelectedSourceStopIds([]);
    setSelectedStop(null);
    setStops([]);
    setNewStop(emptyNewStop());
    setStopMode('existing');
    resetMutation();
  };

  const openModal = () => {
    resetForm();
    setIsOpen(true);
  };

  const openTemplate = (route) => {
    resetForm();
    setCreationMode('source');
    setSourceRouteId(route._id);
    setName(route.name);
    setIsOpen(true);
  };

  const closeModal = () => {
    if (isLoading) return;
    setIsOpen(false);
    resetForm();
  };

  const addExistingStop = () => {
    if (!selectedStop) return;
    setStops([...stops, {
      existingStopId: selectedStop._id,
      label: selectedStop.stopName,
      type: 'existing',
    }]);
    setSelectedStop(null);
  };

  const addNewStop = () => {
    if (!newStopComplete) return;
    const {
      customerNo,
      customerName,
      street,
      zip,
      place,
      latitude,
      longitude,
    } = newStop;
    setStops([...stops, {
      newStop: {
        customerNo,
        customerName,
        address: { street, zip, place },
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
      label: customerName,
      type: 'new',
    }]);
    setNewStop(emptyNewStop());
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, stopIndex) => stopIndex !== index));
  };

  const selectSourceRoute = (event) => {
    const nextRouteId = event.target.value;
    const route = sourceTours.find((candidate) => candidate._id === nextRouteId);
    setSourceRouteId(nextRouteId);
    setSelectedSourceStopIds([]);
    if (route) {
      setName(route.isTestData ? route.name : `${route.name} (Test)`);
    }
  };

  useEffect(() => {
    if (sourceStopData?.routeId === sourceRouteId) {
      setSelectedSourceStopIds(sourceStopData.stops.map((stop) => stop._id));
    }
  }, [sourceRouteId, sourceStopData]);

  const toggleSourceStop = (stopId) => {
    setSelectedSourceStopIds((current) => (
      current.includes(stopId)
        ? current.filter((selectedId) => selectedId !== stopId)
        : [...current, stopId]
    ));
  };

  const toggleAutoGeneration = async (route) => {
    try {
      await patchTestTourTemplate({
        routeId: route._id,
        autoGenerate: !route.testDataAutoGenerate,
      }).unwrap();
    } catch {
      // The current value remains visible when saving the schedule fails.
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete || isDeletingTemplate) return;
    const routeId = templateToDelete._id;
    setTemplateToDelete(null);
    try {
      await archiveTestTour(routeId).unwrap();
    } catch {
      // The archive mutation displays the actionable error toast.
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      await createDailyData({
        houseId: house._id,
        date: `${date}T12:00:00.000Z`,
        name: name.trim(),
        freightOrderId: house.isSAPHouse ? freightOrderId.trim() : undefined,
        sourceRouteId: creationMode === 'source' ? sourceRouteId : undefined,
        sourceStopIds: creationMode === 'source' ? selectedSourceStopIds : undefined,
        stops: creationMode === 'custom'
          ? stops.map((stop) => {
            if (stop.existingStopId) {
              return { existingStopId: stop.existingStopId };
            }
            return { newStop: stop.newStop };
          })
          : undefined,
      }).unwrap();
      setIsOpen(false);
      resetForm();
    } catch {
      // The mutation state renders the actionable error without closing the form.
    }
  };

  const updateNewStop = (field) => (event) => {
    setNewStop({ ...newStop, [field]: event.target.value });
  };

  let footerHint = t('Mindestens einen Stopp hinzufügen');
  if (creationMode === 'source') {
    footerHint = selectedSourceRoute
      ? t('{{count}} Stopps aus der Tour übernehmen', { count: selectedSourceStopIds.length })
      : t('Eine bestehende Tour auswählen');
  } else if (stops.length) {
    footerHint = t('{{count}} Stopps ausgewählt', { count: stops.length });
  }

  return (
    <>
      <Card
        className={styles.card}
        title={t('Test-Tagesdaten')}
        autoHeight
      >
        <div className={styles.cardContent}>
          <div className={styles.cardIntro}>
            <div className={styles.cardIcon}>
              <PlusIcon />
            </div>
            <div>
              <div className={styles.cardHeadline}>{t('Testtour für die Fahrer-App erstellen')}</div>
              <p className={styles.cardDescription}>
                {t('Stelle eine Tour mit vorhandenen oder neuen Teststopps für ein bestimmtes Datum zusammen.')}
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.openButton}
            onClick={openModal}
          >
            <PlusIcon />
            {t('Testtour erstellen')}
          </button>
        </div>
        <div className={styles.templateManager}>
          <div className={styles.templateHeader}>
            <div>
              <strong>{t('Automatische Testtouren')}</strong>
              <span>
                {t('Aktivierte Testtouren werden täglich um 00:05 Uhr für dieses Haus erzeugt.')}
              </span>
            </div>
            <span className={styles.templateCount}>{testTours.length}</span>
          </div>
          {testTours.length === 0 ? (
            <div className={styles.noTemplates}>
              {t('Noch keine Testtour vorhanden. Erstelle eine individuelle Tour oder klone eine bestehende Tour.')}
            </div>
          ) : (
            <div className={styles.templateList}>
              {testTours.map((route) => (
                <div className={styles.templateRow} key={route._id}>
                  <div className={styles.templateInfo}>
                    <strong>{route.name}</strong>
                    <span>
                      {route.testDataSourceRouteId
                        ? t('Sichere Kopie einer bestehenden Tour')
                        : t('Individuelle Testtour')}
                      {' · '}
                      {t('{{count}} Stopps', { count: route.stops })}
                    </span>
                  </div>
                  <div className={styles.templateActions}>
                    <button
                      type="button"
                      className={styles.generateButton}
                      onClick={() => openTemplate(route)}
                    >
                      {t('Für Datum erzeugen')}
                    </button>
                    <label className={styles.scheduleToggle} htmlFor={`test-tour-schedule-${route._id}`}>
                      <span>{route.testDataAutoGenerate ? t('Täglich aktiv') : t('Nicht automatisch')}</span>
                      <input
                        id={`test-tour-schedule-${route._id}`}
                        type="checkbox"
                        checked={route.testDataAutoGenerate}
                        disabled={isSavingSchedule}
                        onChange={() => toggleAutoGeneration(route)}
                      />
                      <i />
                    </label>
                    <button
                      type="button"
                      className={styles.deleteTemplateButton}
                      onClick={() => setTemplateToDelete(route)}
                      disabled={isDeletingTemplate}
                      aria-label={t('Testtour entfernen')}
                      title={t('Testtour entfernen')}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!house.testDailyDataEnabled && (
            <div className={styles.masterSwitchHint}>
              {t('Die automatische Erzeugung ist in der SAP-Konfiguration für dieses Haus ausgeschaltet.')}
            </div>
          )}
          {scheduleError && (
            <div
              className={styles.scheduleError}
              role="alert"
            >
              {t('Die automatische Einstellung konnte nicht gespeichert werden.')}
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        className={styles.modal}
        overlayClassName={styles.overlay}
        contentLabel={t('Testtour erstellen')}
        shouldCloseOnOverlayClick={!isLoading}
      >
        <form
          className={styles.form}
          onSubmit={submit}
        >
          <header className={styles.modalHeader}>
            <div>
              <div className={styles.eyebrow}>{t('Testwerkzeuge')}</div>
              <h2>{t('Neue Testtour erstellen')}</h2>
              <p>{t('Tourdetails festlegen, Stopps auswählen und direkt für die Fahrer-App bereitstellen.')}</p>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={closeModal}
              aria-label={t('Schließen')}
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </header>

          <div className={styles.modalBody}>
            <section className={styles.detailsSection}>
              <div className={styles.sectionTitle}>
                <span>1</span>
                <div>
                  <h3>{t('Tourdetails')}</h3>
                  <p>{t('Wann und unter welchem Namen soll die Tour erscheinen?')}</p>
                </div>
              </div>
              <div className={styles.creationMode}>
                <button
                  type="button"
                  className={creationMode === 'custom' ? styles.activeCreationMode : ''}
                  onClick={() => setCreationMode('custom')}
                  aria-pressed={creationMode === 'custom'}
                >
                  <strong>{t('Individuell zusammenstellen')}</strong>
                  <span>{t('Stopps einzeln auswählen oder neu anlegen')}</span>
                </button>
                <button
                  type="button"
                  className={creationMode === 'source' ? styles.activeCreationMode : ''}
                  onClick={() => setCreationMode('source')}
                  aria-pressed={creationMode === 'source'}
                >
                  <strong>{t('Bestehende Tour verwenden')}</strong>
                  <span>{t('Eine sichere Testkopie mit allen Stopps erstellen')}</span>
                </button>
              </div>
              {creationMode === 'source' && (
                <label className={styles.sourceRouteField} htmlFor="test-daily-data-source-route">
                  {t('Tour auswählen')}
                  <select
                    id="test-daily-data-source-route"
                    value={sourceRouteId}
                    onChange={selectSourceRoute}
                  >
                    <option value="">{t('Bitte Tour auswählen...')}</option>
                    {sourceTours.map((route) => (
                      <option value={route._id} key={route._id}>
                        {route.name}
                        {route.isTestData ? ` · ${t('Testtour')}` : ''}
                        {` · ${t('{{count}} Stopps', { count: route.stops })}`}
                      </option>
                    ))}
                  </select>
                  <span>
                    {selectedSourceRoute?.isTestData
                      ? t('Diese Testtour wird für das gewählte Datum erneut erzeugt.')
                      : t('Die Originaltour bleibt unverändert; es wird eine verknüpfte Testkopie angelegt.')}
                  </span>
                </label>
              )}
              {creationMode === 'source' && sourceRouteId && (
                <div className={styles.sourceStopSelector}>
                  <div className={styles.sourceStopHeader}>
                    <div>
                      <strong>{t('Stopps für die Testtour')}</strong>
                      <span>
                        {t('{{selected}} von {{total}} ausgewählt', {
                          selected: selectedSourceStopIds.length,
                          total: sourceStops.length,
                        })}
                      </span>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => setSelectedSourceStopIds(sourceStops.map((stop) => stop._id))}
                        disabled={!sourceStops.length}
                      >
                        {t('Alle auswählen')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedSourceStopIds([])}
                        disabled={!sourceStops.length}
                      >
                        {t('Auswahl aufheben')}
                      </button>
                    </div>
                  </div>
                  {isFetchingSourceStops ? (
                    <div className={styles.sourceStopLoading}>{t('Stopps werden geladen...')}</div>
                  ) : (
                    <ol className={styles.sourceStops}>
                      {sourceStops.map((stop, index) => (
                        <li key={stop._id}>
                          <label htmlFor={`test-source-stop-${stop._id}`}>
                            <input
                              id={`test-source-stop-${stop._id}`}
                              type="checkbox"
                              checked={selectedSourceStopIds.includes(stop._id)}
                              onChange={() => toggleSourceStop(stop._id)}
                            />
                            <span className={styles.sourceStopOrder}>{index + 1}</span>
                            <span>
                              <strong>{stop.customerName || stop.stopName || stop.customerNo}</strong>
                              <small>
                                {stop.customerNo}
                                {stop.address?.street ? ` · ${stop.address.street}` : ''}
                                {stop.address?.place ? `, ${stop.address.place}` : ''}
                              </small>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ol>
                  )}
                  {!isFetchingSourceStops && sourceStops.length === 0 && (
                    <div className={styles.sourceStopLoading}>
                      {t('Diese Tour enthält keine verfügbaren Stopps.')}
                    </div>
                  )}
                  <p>{t('Die Reihenfolge der Originaltour bleibt erhalten. Die Auswahl wird im Testtemplate gespeichert.')}</p>
                </div>
              )}
              <div className={styles.fields}>
                <label htmlFor="test-daily-data-date">
                  {t('Datum')}
                  <input
                    id="test-daily-data-date"
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                  />
                </label>
                <label htmlFor="test-daily-data-name">
                  {t('Tourname')}
                  <CardTextInput
                    id="test-daily-data-name"
                    value={name}
                    disabled={selectedSourceRoute?.isTestData}
                    onChange={(event) => setName(event.target.value)}
                  />
                </label>
                {house.isSAPHouse && (
                  <label htmlFor="test-daily-data-freight-order-id">
                    {t('Frachtauftrag')}
                    <CardTextInput
                      id="test-daily-data-freight-order-id"
                      value={freightOrderId}
                      onChange={(event) => setFreightOrderId(event.target.value)}
                    />
                  </label>
                )}
              </div>
            </section>

            {creationMode === 'custom' && (
              <div className={styles.stopWorkspace}>
              <section className={styles.stopBuilder}>
                <div className={styles.sectionTitle}>
                  <span>2</span>
                  <div>
                    <h3>{t('Stopps hinzufügen')}</h3>
                    <p>{t('Nutze vorhandene Stopps oder lege einen georeferenzierten Teststopp an.')}</p>
                  </div>
                </div>

                <div className={styles.modeSwitch}>
                  <button
                    type="button"
                    className={stopMode === 'existing' ? styles.activeMode : ''}
                    onClick={() => setStopMode('existing')}
                    aria-pressed={stopMode === 'existing'}
                  >
                    {t('Vorhandener Stopp')}
                  </button>
                  <button
                    type="button"
                    className={stopMode === 'new' ? styles.activeMode : ''}
                    onClick={() => setStopMode('new')}
                    aria-pressed={stopMode === 'new'}
                  >
                    {t('Neuer Teststopp')}
                  </button>
                </div>

                {stopMode === 'existing' && (
                  <div className={styles.existingStop}>
                    <label htmlFor="test-daily-data-existing-stop">{t('Stopp suchen')}</label>
                    <div className={styles.addRow}>
                      <StopSelect
                        inputId="test-daily-data-existing-stop"
                        value={selectedStop}
                        onChange={setSelectedStop}
                        excludeStopIds={selectedIds}
                        houseId={house._id}
                      />
                      <button
                        type="button"
                        className={styles.addButton}
                        onClick={addExistingStop}
                        disabled={!selectedStop}
                      >
                        <PlusIcon />
                        {t('Hinzufügen')}
                      </button>
                    </div>
                    <p className={styles.hint}>
                      {t('Der ausgewählte Stopp bleibt unverändert und wird nur dieser Testtour zugeordnet.')}
                    </p>
                  </div>
                )}

                {stopMode === 'new' && (
                  <div className={styles.newStop}>
                    <div className={styles.newStopFields}>
                      {[
                        ['customerNo', 'Kundennummer'],
                        ['customerName', 'Name'],
                        ['street', 'Straße'],
                        ['zip', 'PLZ'],
                        ['place', 'Ort'],
                        ['latitude', 'Breitengrad'],
                        ['longitude', 'Längengrad'],
                      ].map(([field, label]) => (
                        <label
                          key={field}
                          htmlFor={`test-daily-data-${field}`}
                        >
                          {t(label)}
                          <CardTextInput
                            id={`test-daily-data-${field}`}
                            value={newStop[field]}
                            onChange={updateNewStop(field)}
                            type={field === 'latitude' || field === 'longitude' ? 'number' : 'text'}
                            step={field === 'latitude' || field === 'longitude' ? 'any' : undefined}
                          />
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.addButton}
                      onClick={addNewStop}
                      disabled={!newStopComplete}
                    >
                      <PlusIcon />
                      {t('Teststopp übernehmen')}
                    </button>
                  </div>
                )}
              </section>

              <aside className={styles.stopSummary}>
                <div className={styles.summaryHeader}>
                  <div>
                    <span className={styles.summaryStep}>3</span>
                    <h3>{t('Tourstopps')}</h3>
                  </div>
                  <span className={styles.stopCount}>{stops.length}</span>
                </div>

                {stops.length === 0 ? (
                  <div className={styles.emptyStops}>
                    <div className={styles.emptyIcon}>
                      <PlusIcon />
                    </div>
                    <strong>{t('Noch keine Stopps')}</strong>
                    <span>{t('Füge links mindestens einen Stopp hinzu.')}</span>
                  </div>
                ) : (
                  <ol className={styles.stops}>
                    {stops.map((stop, index) => (
                      // Stops may have identical customer data, so their position disambiguates the key.
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={`${stop.label}-${index}`}>
                        <span className={styles.stopNumber}>{index + 1}</span>
                        <div>
                          <strong>{stop.label}</strong>
                          <span>{stop.type === 'new' ? t('Neuer Teststopp') : t('Vorhandener Stopp')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          aria-label={t('Stopp entfernen')}
                        >
                          <CloseIcon />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </aside>
              </div>
            )}

            {error && (
              <div
                className={styles.error}
                role="alert"
              >
                {t('Die Testtour konnte nicht erstellt werden. Bitte Eingaben prüfen und erneut versuchen.')}
              </div>
            )}
          </div>

          <footer className={styles.modalFooter}>
            <span>{footerHint}</span>
            <div>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={closeModal}
                disabled={isLoading}
              >
                {t('Abbrechen')}
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={!canSubmit || isLoading}
              >
                {isLoading ? <LoaderIcon /> : <PlusIcon />}
                {isLoading ? t('Wird erstellt...') : t('Testtour erstellen')}
              </button>
            </div>
          </footer>
        </form>
      </Modal>
      <DeleteModal
        isOpen={Boolean(templateToDelete)}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={confirmDeleteTemplate}
        title={t('Testtour entfernen')}
        content={t('Die Testtour {{name}} wird aus der automatischen Verwaltung entfernt. Bereits erzeugte Tagesdaten bleiben erhalten.', {
          name: templateToDelete?.name || '',
        })}
      />
    </>
  );
};

TestDailyDataCard.propTypes = {
  house: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    isSAPHouse: PropTypes.bool,
    testDailyDataEnabled: PropTypes.bool,
  }).isRequired,
};

export default TestDailyDataCard;
